import { EV } from '../core/eventbus.js';
import { Repo } from '../data/schema.js';
import { Tags } from './tags.js';
import { FRAGMENTS, TRACKS, REALMS, CHANNEL_COST, realmOf, realmName, needCombos } from '../data/inspire.js';
import { TAG_DEFS } from '../data/tags.js';

/**
 * 灵感与悟道：需求里那句「系统不说话，但一直在看」。
 *
 * 三件事：
 *   1. 碎片 — 哪个标签攒到份上，心里冒出半句话（poke，攒一次看一次）
 *   2. 门路 — 两块碎片撞上了，可能突然明白一件事，得一张资格证（一天只推一次）
 *   3. 境界 — 灵气攒出来的修行进度，反过来让吐纳更沉、念头更容易聚拢
 *
 * 玩家看不见任何阈值，只看见「你忽然明白了什么」。
 */

const MAX_QI = 999;

function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

export const Inspire = {
  /* ------------------------------ 碎 片 ------------------------------ */

  /** 手里已有的碎片（key 列表） */
  ownedFrags(db) {
    return db.table('insights').whereEq('kind', 'fragment').map((r) => r.key);
  },

  /** 攒过一次之后看一眼：有新念头冒出来吗 */
  poke(db, events) {
    const owned = new Set(this.ownedFrags(db));
    const w = Repo.getWorld(db);
    const got = [];
    for (const [id, def] of Object.entries(FRAGMENTS)) {
      if (owned.has(id)) continue;
      const enough = Object.entries(def.need)
        .every(([tag, n]) => Tags.get(db, tag).total >= n);
      if (!enough) continue;
      db.table('insights').insert({
        kind: 'fragment', key: id, name: def.name, sense: def.sense, day: w.day,
      });
      got.push(def);
      if (events) {
        events.emit(EV.INSIGHT, { kind: 'fragment', name: def.name, text: def.sense });
      }
    }
    return got;
  },

  /* ------------------------------ 门 路 ------------------------------ */


  /** 一天只推一回：两块碎片相遇，可能想通一件事 */
  tickDaily(db, events) {
    const w = Repo.getWorld(db);
    if (Repo.getFlag(db, `insight:${w.day}`)) return [];
    Repo.setFlag(db, `insight:${w.day}`, true);

    this.poke(db, events);
    const owned = new Set(this.ownedFrags(db));
    if (owned.size < 2) return [];

    const p = Repo.getPlayer(db);
    const realm = this.realm(db);
    // 三样加成：身上的灵气、与人为善、修行境界；喝了清心汤那一天更容易想通
    const bonus = clamp((p.qi || 0) / MAX_QI, 0, 1) * 0.15
      + clamp(Tags.get(db, 'aid').total / 120, 0, 1) * 0.12
      + clamp(Tags.get(db, 'trustOfLocals').total / 120, 0, 1) * 0.08
      + realm.level * 0.05
      + this.perks(db, 'scholar').insightBonus
      + (Repo.getFlag(db, `clearHeart:${w.day}`) ? 0.2 : 0);

    const out = [];
    for (const track of Object.values(TRACKS)) {
      for (let i = 0; i < track.levels.length; i += 1) {
        const lv = track.levels[i];
        if (Tags.has(db, lv.cert)) break;                    // 这一级已得过，等下一级
        if (i > 0 && !Tags.has(db, track.levels[i - 1].cert)) break; // 上一级还没到
        // 碎片没凑齐（同一件事可能有几条想通的路子，任一条凑齐就行）
        const hit = needCombos(lv).find((combo) => combo.every((f) => owned.has(f)));
        if (!hit) break;
        if (Math.random() < lv.rate + bonus) {
          Tags.grantCert(db, lv.cert, 'insight');
          Repo.log(db, `忽然明白了：${lv.say}`, 'insight', w.day, w.minute);
          if (events) {
            events.emit(EV.INSIGHT, { kind: 'cert', name: lv.name, text: lv.say, track: track.name });
          }
          out.push(lv);
        }
        break;                                                // 一门一天只推一级
      }
    }
    return out;
  },

  /* ------------------------------ 灵气与境界 ------------------------------ */

  /** 灵气入账：同时记在「累计得过多少」上，够了就上一层境界 */
  addQi(db, n, events) {
    if (!(n > 0)) return null;
    const ever = Number(Repo.getFlag(db, 'qi_ever')) || 0;
    const next = ever + n;
    Repo.setFlag(db, 'qi_ever', next);
    const r = realmOf(next);
    const cur = Number(Repo.getFlag(db, 'realm')) || 0;
    if (r && r.level > cur) {
      Repo.setFlag(db, 'realm', r.level);
      if (events) {
        events.emit(EV.INSIGHT, { kind: 'realm', name: r.name, text: r.say, perk: r.perk });
      }
      return r;
    }
    return null;
  },

  /** 当前境界：{ level, name, next, need } */
  realm(db) {
    const ever = Number(Repo.getFlag(db, 'qi_ever')) || 0;
    const level = Number(Repo.getFlag(db, 'realm')) || 0;
    const next = REALMS.find((r) => r.level === level + 1) || null;
    return {
      level,
      name: realmName(level),
      ever,
      next: next ? next.name : null,
      need: next ? next.need : null,
    };
  },

  /** 吐纳一口气长多少灵气：境界越高越沉 */
  qiPerBreath(db) {
    return 1 + this.realm(db).level;
  },

  /* ------------------------------ 御气 ------------------------------ */

  /** 一畦苗能不能用灵气催一催：{ ok, cost, reason } */
  canChannel(db, prop) {
    const st = (prop && prop.state) || {};
    if (!(st.stage >= 1 && st.stage < 3)) return { ok: false, reason: '地里没长着东西，催它作甚。' };
    const realm = this.realm(db).level;
    // 修行不到「感气」，手里就没有可御的气——这是修行头一桩用处
    if (realm < 1) return { ok: false, reason: '你还摸不着气。先去蒲团上坐坐，坐够了自然知道怎么使。' };
    const off = this.perks(db, 'farm').channelOff || 0;
    const cost = Math.max(10, (realm >= 3 ? 15 : CHANNEL_COST) - off);
    const p = Repo.getPlayer(db);
    if ((p.qi || 0) < cost) return { ok: false, reason: `气不够，还得${cost}点灵气。` };
    return { ok: true, cost };
  },

  /** 御气催熟：一口气把这一畦往前推一段（不收灵气之外的钱） */
  channel(db, prop) {
    const chk = this.canChannel(db, prop);
    if (!chk.ok) return chk;
    const p = Repo.getPlayer(db);
    const st = { ...(prop.state || {}) };
    const nextStage = Math.min(3, st.stage + 1);
    prop.state = { ...st, stage: nextStage, watered: false };
    db.table('props').update(prop.id, { state: prop.state });
    Repo.savePlayer(db, {
      qi: Math.max(0, (p.qi || 0) - chk.cost),
      energy: Math.max(0, (p.energy || 0) - 2),
      mood: Math.min(100, (p.mood || 0) + 2),
    });
    Tags.gain(db, 'farming', 1, 'channel', 10);
    const text = nextStage >= 3
      ? '一口气渡过去，菜心已经抱紧，明日就能收。'
      : '掌心贴着土，青苗蹿了一截。';
    return { ok: true, text, cost: chk.cost };
  },

  /* ------------------------------ 灶上 ------------------------------ */

  /** 这一道菜做不做得成：火候差多少就失手多少，灶上那张照再打个折 */
  cookOdds(db, recipe) {
    const heat = Tags.get(db, 'restaurant').total;
    const need = recipe.heat || 0;
    const realm = this.realm(db).level;
    // 火候够则几乎不失手；差得越多越悬，境界高一点手更稳
    const fail = need <= 0 ? 0.04 : clamp((need - heat) / (need * 1.6), 0.05, 0.6) - realm * 0.02;
    return { fail: clamp(fail * (this.perks(db, 'cook').cookFail || 1), 0.02, 0.6), heat, need };
  },

  /* ------------------------------ 照 的 兑 现 ------------------------------ */
  /* 有进必有出：照不是摆着看的，每一级都换回一点真东西 */

  /** 这一门手上最高第几级（0 = 还没入门） */
  levelOf(db, trackId) {
    const t = TRACKS[trackId];
    if (!t) return 0;
    let n = 0;
    for (const lv of t.levels) {
      if (!Tags.has(db, lv.cert)) break;
      n += 1;
    }
    return n;
  },

  /** 已得各级的 perk 合并：折扣率相乘，加成相加，解锁并集 */
  perks(db, trackId) {
    const out = {
      unlock: [], leftOver: 0,
      cookFail: 1, cookEffect: 1,
      yield: 0, seedRate: 0, channelOff: 0,
      sellBonus: 0, buyOff: 0,
      craftOff: 0, qiBonus: 0, insightBonus: 0,
    };
    const t = TRACKS[trackId];
    if (!t) return out;
    for (const lv of t.levels) {
      if (!Tags.has(db, lv.cert)) break;
      const pk = lv.perk || {};
      if (pk.cookFail) out.cookFail *= pk.cookFail;
      if (pk.cookEffect) out.cookEffect = Math.max(out.cookEffect, pk.cookEffect);
      if (pk.leftOver) out.leftOver = Math.max(out.leftOver, pk.leftOver);
      if (pk.unlock) out.unlock.push(...pk.unlock);
      for (const k of ['yield', 'seedRate', 'channelOff', 'sellBonus', 'buyOff', 'craftOff', 'qiBonus', 'insightBonus']) {
        if (pk[k]) out[k] += pk[k];
      }
    }
    return out;
  },

  /** 讲究的菜与丹：手上那张照够不够做这一味 */
  canMake(db, id) {
    return this.perks(db, 'cook').unlock.includes(id);
  },

  /**
   * 买卖时的议价：行商那张照 + 身上的气度。
   * 气度（aura）从此有了去处——人干净、坐得住，说话就值钱一点。
   */
  bargain(db, side) {
    const pk = this.perks(db, 'trade');
    const aura = clamp(Number(Repo.getPlayer(db).aura) || 0, 0, 1);
    if (side === 'sell') return 1 + pk.sellBonus + aura * 0.04;
    return 1 - Math.min(0.2, pk.buyOff + aura * 0.02);
  },

  /* ------------------------------ 炼 丹 ------------------------------ */

  /** 丹成不成：看心里静不静、修行到哪一步 */
  pillOdds(db, pill) {
    const calm = Tags.get(db, 'insight').total;
    const realm = this.realm(db).level;
    const fail = clamp(0.4 - calm / 200 - realm * 0.06 - (pill.aid || 0), 0.05, 0.55);
    return { fail: clamp(fail, 0.05, 0.55) };
  },

  /** 能不能起火炼这一味：修行不到，丹火点不着；手上没照，讲究的做不出来 */
  canRefine(db, pill) {
    const realm = this.realm(db).level;
    if (realm < (pill.minRealm || 1)) {
      return { ok: false, reason: '丹火点不着——你还摸不着气，先去蒲团上坐坐。' };
    }
    if (pill.cert && !Tags.has(db, pill.cert)) {
      return { ok: false, reason: '这一味还得再练练火候，手上没那张照，做不出来。' };
    }
    const p = Repo.getPlayer(db);
    if ((p.qi || 0) < (pill.qi || 0)) {
      return { ok: false, reason: `灵气不够，这一味得${pill.qi}点。` };
    }
    return { ok: true };
  },

  /* ------------------------------ 内 视 ------------------------------ */

  /**
   * 照心镜里看见的自己：只说「做得下去了 / 算得上熟手 / 已经老到」这类话，
   * 一个数都不给。修行越深，看得越细——这是修行给的唯一回报：看见自己。
   */
  introspect(db) {
    const realm = this.realm(db);
    if (realm.level < 1) {
      return ['对着镜子坐了坐，只看见一张平常的脸。', '（心还不静，照不见什么——先去蒲团上坐坐。）'];
    }
    const word = (v) => (v >= 80 ? '已经老到' : v >= 50 ? '算得上熟手' : v >= 20 ? '做得下去了' : v > 0 ? '刚摸着点门道' : null);
    // 只说那些「能长出念头」的事——它们才是通向下一张照的路
    const shown = new Set();
    for (const def of Object.values(FRAGMENTS)) {
      for (const t of Object.keys(def.need)) shown.add(t);
    }
    const rows = [];
    for (const t of shown) {
      const v = Tags.get(db, t).total;
      const w = word(v);
      if (w) rows.push([v, `${TAG_DEFS[t] ? TAG_DEFS[t].name : t}：${w}`]);
    }
    rows.sort((a, b) => b[0] - a[0]);
    const out = rows.slice(0, realm.level >= 3 ? 5 : 3).map((r) => r[1]);
    if (!out.length) out.push('心里还是一张白纸，日子过着过着会写上去的。');

    // 快要想通的那样：只说「跟什么有关」，不说是哪一句
    const owned = new Set(this.ownedFrags(db));
    let best = null;
    let bestRatio = 0;
    for (const [id, def] of Object.entries(FRAGMENTS)) {
      if (owned.has(id)) continue;
      let ratio = 1;
      for (const [tag, n] of Object.entries(def.need)) {
        ratio = Math.min(ratio, Tags.get(db, tag).total / n);
      }
      if (ratio > bestRatio) { bestRatio = ratio; best = def; }
    }
    const near = realm.level >= 3 ? 0.4 : 0.6;
    if (best && bestRatio >= near) {
      const t = Object.keys(best.need)[0];
      out.push(`有一样快要想通了，好像跟「${TAG_DEFS[t] ? TAG_DEFS[t].name : t}」脱不了干系。`);
    }
    return out;
  },
};
